import { useState, type SubmitEventHandler } from 'react';

import type { TeamProfile } from '@/core/schemas';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { FormField } from '@/shared/ui/FormField';

import {
  emptyTeamForm,
  snippetDuration,
  validateTeamForm,
  type FieldErrors,
  type TeamFormValues,
} from './teamFormModel';

interface TeamFormProps {
  initialValues?: TeamFormValues;
  songMaxDurationSeconds: number;
  submitLabel: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (profile: TeamProfile) => void;
}

export function TeamForm({
  initialValues = emptyTeamForm,
  songMaxDurationSeconds,
  submitLabel,
  isSubmitting,
  errorMessage,
  onSubmit,
}: TeamFormProps) {
  const [values, setValues] = useState<TeamFormValues>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const update = (field: keyof TeamFormValues) => (event: { target: { value: string } }) => {
    const next = { ...values, [field]: event.target.value };
    setValues(next);

    // Only revalidate after a first attempt, so nothing turns red while the
    // captain is still typing the very first character.
    if (wasSubmitted) {
      const result = validateTeamForm(next, songMaxDurationSeconds);
      setErrors(result.ok ? {} : result.errors);
    }
  };

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    setWasSubmitted(true);

    const result = validateTeamForm(values, songMaxDurationSeconds);

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    onSubmit(result.profile);
  };

  const duration = snippetDuration(values);

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      {errorMessage !== null && <Alert tone="danger">{errorMessage}</Alert>}

      <fieldset className="flex flex-col gap-4" disabled={isSubmitting}>
        <legend className="sr-only">Team</legend>

        <FormField
          label="Feuerwehr"
          value={values.fireDepartment}
          onChange={update('fireDepartment')}
          error={errors.fireDepartment}
          placeholder="z. B. Freiwillige Feuerwehr Ratekau"
          autoComplete="organization"
          hint="Mehrere Teams derselben Feuerwehr sind erlaubt."
        />

        <FormField
          label="Teamname"
          value={values.name}
          onChange={update('name')}
          error={errors.name}
          placeholder="z. B. Löschzug 1"
          hint="Der Teamname muss im gesamten Turnier eindeutig sein."
        />
      </fieldset>

      <fieldset className="flex flex-col gap-4" disabled={isSubmitting}>
        <legend className="text-fg mb-1 text-sm font-semibold">Einlaufsong</legend>

        <FormField
          label="Titel"
          value={values.songTitle}
          onChange={update('songTitle')}
          error={errors.songTitle}
          placeholder="z. B. Highway to Hell"
        />

        <FormField
          label="Interpret (optional)"
          value={values.songArtist}
          onChange={update('songArtist')}
          error={errors.songArtist}
          placeholder="z. B. AC/DC"
        />

        <FormField
          label="Link zum Song (optional)"
          type="url"
          inputMode="url"
          value={values.songUrl}
          onChange={update('songUrl')}
          error={errors.songUrl}
          placeholder="https://…"
          hint="Hilft der Moderation, den richtigen Titel zu finden."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Startsekunde"
            type="number"
            inputMode="numeric"
            min={0}
            value={values.startSeconds}
            onChange={update('startSeconds')}
            error={errors.startSeconds}
            placeholder="0"
          />

          <FormField
            label="Endsekunde"
            type="number"
            inputMode="numeric"
            min={1}
            value={values.endSeconds}
            onChange={update('endSeconds')}
            error={errors.endSeconds}
            placeholder={String(songMaxDurationSeconds)}
          />
        </div>

        <SnippetHint duration={duration} maximum={songMaxDurationSeconds} />
      </fieldset>

      <Button type="submit" isLoading={isSubmitting} className="sm:self-start">
        {submitLabel}
      </Button>
    </form>
  );
}

function SnippetHint({ duration, maximum }: { duration: number | null; maximum: number }) {
  if (duration === null) {
    return (
      <p className="text-fg-subtle text-sm">
        Der Ausschnitt darf höchstens {maximum} Sekunden lang sein.
      </p>
    );
  }

  const isValid = duration > 0 && duration <= maximum;

  return (
    <p className={isValid ? 'text-success text-sm' : 'text-danger text-sm'}>
      {duration > 0
        ? `Ausschnitt: ${duration} von maximal ${maximum} Sekunden`
        : 'Die Endsekunde muss nach der Startsekunde liegen.'}
    </p>
  );
}
